import React, { useEffect, useRef, useState } from 'react';
import DataStateNotice from './DataStateNotice';
import { exportRequest } from '../../services/activityDistributionsApi';
import type { ActivitiesRequest } from '../../services/activityDistributionsApi';
import { describeRequestError, isAbortError } from '../../services/http';
import { downloadCsv } from '../../utils/csv';

export const EXPORT_ALL_ROWS_LABEL = 'Export all rows (CSV)';

interface ExportAllRowsButtonProps {
  request: ActivitiesRequest;
  filename: string;
}

/**
 * Runs the job on screen as a full export, waits for it and downloads every row as one CSV. When it cannot, it says
 * why under the button and downloads nothing: a preview is never handed over as the whole result.
 */
const ExportAllRowsButton: React.FC<ExportAllRowsButtonProps> = ({ request, filename }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Leaving the page stops waiting (the backend job itself keeps running).
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const handleExport = async () => {
    const c = new AbortController();
    controller.current = c;
    setBusy(true);
    setError(null);
    try {
      downloadCsv(filename, await exportRequest(request, { signal: c.signal }));
    } catch (e) {
      if (isAbortError(e)) return;
      setError(describeRequestError(e));
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={handleExport}>
        {busy ? (
          <>
            <span className="loading loading-spinner loading-xs"></span>
            Exporting all rows...
          </>
        ) : (
          EXPORT_ALL_ROWS_LABEL
        )}
      </button>
      {error ? <DataStateNotice tone="error" title="All rows could not be exported" body={error} /> : null}
    </div>
  );
};

export default ExportAllRowsButton;
